var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import '../styles/helpTool.scss';
import globalStore from '../store';
import CustomScrollbar from './CustomScrollbar';
import ButtonComponent from './ButtonComponent';
import { trackEvent, AnalyticsCategory } from '../helper/analytics';
import { t } from 'i18next';
// Reliable touch-primary detection — works on modern iPads (desktop UA).
// On touch, role="menu"/"menuitem" is dropped (same convention as
// ManipulativeTool.tsx/ContextMenu.tsx) so VoiceOver doesn't announce a
// "menu item, N of M" position count for every row — desktop/keyboard users
// keep the standard menu semantics.
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var HelpTool = function () {
    var _a, _b, _c, _d, _e, _f, _g;
    var _h = globalStore.useStoreState(function (state) { return state.jsonDataStore; }), btnConfig = _h.btnConfig, helpBtn = _h.mainConfig.helpBtn;
    var _j = useState(false), openTool = _j[0], setOpenTool = _j[1];
    var _k = useState(''), announcement = _k[0], setAnnouncement = _k[1];
    var _l = useState(0), activeIndex = _l[0], setActiveIndex = _l[1];
    var panelRef = useRef(null);
    var triggerRef = useRef(null);
    var itemRefs = useRef(new Map());
    var btnData = (_a = helpBtn === null || helpBtn === void 0 ? void 0 : helpBtn.btnList) !== null && _a !== void 0 ? _a : [];
    // External feedback link shown directly under the Help heading. URL is
    // configurable via helpBtn.feedbackUrl in btnData.json; falls back to the
    // public contact page if not configured.
    var FEEDBACK_URL = (_b = helpBtn === null || helpBtn === void 0 ? void 0 : helpBtn.feedbackUrl) !== null && _b !== void 0 ? _b : 'https://mlcmath.com/contact';
    // Build flat list of focusable items — feedback link first, then one entry
    // per video/pdf button (video before pdf within a row), then Exit Menu at
    // the end. Order matches the visual left-to-right, top-to-bottom order in
    // the panel so Arrow/Home/End navigation lands on every button in turn.
    var focusableItems = useMemo(function () {
        var items = [
            { id: 'feedback', label: 'text.commentOrFeedbackContactMLC', kind: 'feedback' },
        ];
        btnData.forEach(function (item) {
            if (item.isHeading)
                return;
            if (!item.video && !item.pdf)
                return;
            items.push({ id: "".concat(item.id, "-label"), label: item.label, kind: 'label' });
            if (item.video)
                items.push({ id: item.id, label: item.label, kind: 'video', url: item.video });
            if (item.pdf)
                items.push({ id: item.id, label: item.label, kind: 'pdf', url: item.pdf });
        });
        items.push({ id: 'exitMenu', label: 'text.exitMenu', kind: 'exit' });
        return items;
    }, [btnData]);
    // Close panel on outside click
    useEffect(function () {
        if (!openTool)
            return;
        var handleClickOutside = function (e) {
            var target = e.target;
            if (panelRef.current &&
                !panelRef.current.contains(target) &&
                triggerRef.current &&
                !triggerRef.current.contains(target)) {
                closePanel();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return function () { return document.removeEventListener('mousedown', handleClickOutside); };
    }, [openTool]);
    // Focus first item when panel opens
    useEffect(function () {
        if (openTool && focusableItems.length > 0) {
            setActiveIndex(0);
            requestAnimationFrame(function () {
                var firstItem = itemRefs.current.get(0);
                if (firstItem)
                    firstItem.focus();
            });
        }
    }, [openTool, focusableItems.length]);
    // Make rest of the page inert when help panel is open (works with VoiceOver)
    useEffect(function () {
        if (!openTool)
            return;
        var panel = panelRef.current;
        if (!panel)
            return;
        // Set inert on all siblings of the panel's parent that aren't the panel
        var inertedElements = [];
        var body = document.body;
        Array.from(body.children).forEach(function (child) {
            var el = child;
            if (el.contains(panel) || el === panel)
                return;
            if (!el.hasAttribute('inert')) {
                el.setAttribute('inert', '');
                el.setAttribute('aria-hidden', 'true');
                inertedElements.push(el);
            }
        });
        // Also inert siblings within the panel's ancestor chain up to body
        var current = panel.parentElement;
        while (current && current !== body) {
            Array.from(current.children).forEach(function (child) {
                var el = child;
                if (el.contains(panel) || el === panel)
                    return;
                if (!el.hasAttribute('inert')) {
                    el.setAttribute('inert', '');
                    el.setAttribute('aria-hidden', 'true');
                    inertedElements.push(el);
                }
            });
            current = current.parentElement;
        }
        // Keyboard focus trap (Tab key)
        var trapFocus = function (e) {
            if (e.key !== 'Tab')
                return;
            var focusedEl = document.activeElement;
            if (!panel.contains(focusedEl)) {
                e.preventDefault();
                var firstItem = itemRefs.current.get(0);
                if (firstItem)
                    firstItem.focus();
            }
        };
        document.addEventListener('keydown', trapFocus, true);
        return function () {
            document.removeEventListener('keydown', trapFocus, true);
            inertedElements.forEach(function (el) {
                el.removeAttribute('inert');
                el.removeAttribute('aria-hidden');
            });
        };
    }, [openTool]);
    var closePanel = useCallback(function () {
        setOpenTool(false);
        setAnnouncement('');
        requestAnimationFrame(function () {
            var _a;
            setAnnouncement(t('text.helpPanelClosed'));
            // Restore focus to trigger button
            var btn = (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button');
            if (btn)
                btn.focus();
        });
    }, []);
    var openPdf = useCallback(function (url, label, id) {
        setAnnouncement('');
        requestAnimationFrame(function () {
            setAnnouncement(t('text.openingPdf', { name: t(label) }));
        });
        // Track which support material (PDF) was opened, keyed by its stable id
        // (e.g. 'helpFractionTiles') — NOT the localized label — so the report
        // ranks one row per material and matches the Ctrl+Alt+I shortcut's name.
        trackEvent(AnalyticsCategory.support, 'openMaterial', id);
        window.open(url, '_blank', 'noopener,noreferrer');
    }, []);
    var openVideo = useCallback(function (url, label, id) {
        setAnnouncement('');
        requestAnimationFrame(function () {
            setAnnouncement(t('text.openingVideo', { name: t(label) }));
        });
        trackEvent(AnalyticsCategory.support, 'openVideoMaterial', id);
        window.open(url, '_blank', 'noopener,noreferrer');
    }, []);
    var focusItem = useCallback(function (index) {
        setActiveIndex(index);
        requestAnimationFrame(function () {
            var el = itemRefs.current.get(index);
            if (el)
                el.focus();
        });
    }, []);
    var handleMenuKeyDown = useCallback(function (e) {
        var _a;
        var count = focusableItems.length;
        if (count === 0)
            return;
        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowRight': {
                e.preventDefault();
                e.stopPropagation();
                var next = (activeIndex + 1) % count;
                focusItem(next);
                break;
            }
            case 'ArrowUp':
            case 'ArrowLeft': {
                e.preventDefault();
                e.stopPropagation();
                var prev = (activeIndex - 1 + count) % count;
                focusItem(prev);
                break;
            }
            case 'Home': {
                e.preventDefault();
                e.stopPropagation();
                focusItem(0);
                break;
            }
            case 'End': {
                e.preventDefault();
                e.stopPropagation();
                focusItem(count - 1);
                break;
            }
            case 'Tab': {
                // Focus trap — cycle within panel. Tab skips 'label' entries (they're
                // reachable via Arrow keys only, not part of Tab order) and stops
                // only on actionable items (feedback/video/pdf/exit).
                e.preventDefault();
                e.stopPropagation();
                var step = e.shiftKey ? -1 : 1;
                var next = activeIndex;
                do {
                    next = (next + step + count) % count;
                } while (((_a = focusableItems[next]) === null || _a === void 0 ? void 0 : _a.kind) === 'label' && next !== activeIndex);
                focusItem(next);
                break;
            }
            case 'Escape': {
                e.preventDefault();
                e.stopPropagation();
                closePanel();
                break;
            }
            case 'Enter':
            case ' ': {
                e.preventDefault();
                e.stopPropagation();
                var item = focusableItems[activeIndex];
                if ((item === null || item === void 0 ? void 0 : item.kind) === 'exit') {
                    closePanel();
                }
                else if ((item === null || item === void 0 ? void 0 : item.kind) === 'feedback') {
                    window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer');
                }
                else if ((item === null || item === void 0 ? void 0 : item.kind) === 'video' && item.url) {
                    openVideo(item.url, item.label, item.id);
                }
                else if ((item === null || item === void 0 ? void 0 : item.kind) === 'pdf' && item.url) {
                    openPdf(item.url, item.label, item.id);
                }
                break;
            }
        }
    }, [activeIndex, focusableItems, focusItem, closePanel, openPdf, openVideo]);
    if (!helpBtn || !btnData.length)
        return null;
    // Build a map from item id to focusable index
    var focusableIdx = 0;
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: triggerRef, style: { display: 'inline-block' }, children: _jsx(ButtonComponent, { defaultCSS: "sidebarBtn sideBar", normalCSS: "settingsBtn", imgSrc: (_c = btnConfig === null || btnConfig === void 0 ? void 0 : btnConfig.help) === null || _c === void 0 ? void 0 : _c.imgSrc, clickHandler: function () {
                        if (openTool) {
                            closePanel();
                        }
                        else {
                            setOpenTool(true);
                            trackEvent(AnalyticsCategory.support, 'openPanel');
                            setAnnouncement('');
                            requestAnimationFrame(function () {
                                setAnnouncement(t('text.helpPanelOpened', { count: focusableItems.length }));
                            });
                        }
                    }, isActive: openTool, controlActiveFromOutside: true, clickActive: true, type: "imageType2", ariaLabel: "text.toggleHelp", ariaExpanded: openTool, ariaControls: openTool ? 'help-panel' : undefined, showToolTip: true, direction: "right", title: "text.help" }) }), openTool && (_jsxs("div", { ref: panelRef, className: "helpTool helpToolOpen", id: "help-panel", role: !IS_TOUCH_DEVICE ? 'menu' : undefined, "aria-label": t('text.help'), onKeyDown: handleMenuKeyDown, style: {
                    top: (_e = (_d = document.querySelector('.appHeader')) === null || _d === void 0 ? void 0 : _d.offsetHeight) !== null && _e !== void 0 ? _e : 60,
                    left: ((_g = (_f = document.querySelector('.sidebar')) === null || _f === void 0 ? void 0 : _f.offsetWidth) !== null && _g !== void 0 ? _g : 55) + 5,
                    '--help-arrow-top': (function () {
                        var trigger = triggerRef.current;
                        var header = document.querySelector('.appHeader');
                        if (trigger && header) {
                            var triggerRect = trigger.getBoundingClientRect();
                            var panelTop = header.offsetHeight;
                            var arrowY = triggerRect.top + triggerRect.height / 2 - panelTop - 10;
                            return "".concat(Math.max(14, arrowY), "px");
                        }
                        return '14px';
                    })(),
                }, children: [_jsx("div", { className: "helpArrow", "aria-hidden": "true" }), _jsxs("div", { className: "headerContainer", children: [_jsx("div", { className: "btnWithHeading", children: _jsxs("div", { className: "headingContent", children: [_jsx("div", { "aria-hidden": "true", children: t('text.help') }), (function () {
                                            var feedbackIdx = focusableIdx++;
                                            return (_jsx("a", { href: FEEDBACK_URL, target: "_blank", rel: "noopener noreferrer", className: "helpFeedbackLink".concat(feedbackIdx === activeIndex ? ' helpFeedbackLink--active' : ''), role: IS_TOUCH_DEVICE ? 'presentation' : 'menuitem', tabIndex: feedbackIdx === activeIndex ? 0 : -1, ref: function (el) {
                                                    if (el)
                                                        itemRefs.current.set(feedbackIdx, el);
                                                }, onFocus: function () { return setActiveIndex(feedbackIdx); }, children: t('text.commentOrFeedbackContactMLC') }));
                                        })()] }) }), _jsx("div", { className: "footerModal", "aria-hidden": "true", children: _jsx("img", { src: "./assets/images/shareColourPatches.svg", tabIndex: -1, alt: "", "aria-hidden": "true" }) })] }), _jsx(CustomScrollbar, { className: "helpScrollbar helpScrollbarOpen", style: { width: '100%' }, direction: "vertical", children: _jsxs("div", { id: "help-menu", children: [btnData.map(function (item) {
                                    if (item.isHeading) {
                                        return (_jsx("div", { className: "helpSectionHeading", role: "separator", "aria-hidden": "true", children: _jsx("span", { className: "text", children: t(item.label) }) }, item.id));
                                    }
                                    if (!item.pdf && !item.video)
                                        return null;
                                    var labelIdx = focusableIdx++;
                                    var videoIdx = item.video ? focusableIdx++ : -1;
                                    var pdfIdx = item.pdf ? focusableIdx++ : -1;
                                    return (_jsx("div", { className: "helpBtnHolder", children: _jsxs("div", { className: "contentHolder", children: [_jsx("div", { className: "text", role: IS_TOUCH_DEVICE ? 'presentation' : 'menuitem', tabIndex: labelIdx === activeIndex ? 0 : -1, ref: function (el) {
                                                        if (el)
                                                            itemRefs.current.set(labelIdx, el);
                                                    }, onFocus: function () { return setActiveIndex(labelIdx); }, children: t(item.label) }), _jsxs("div", { className: "helpIconGroup", children: [item.video && (_jsx(ButtonComponent, { normalCSS: "helpIconBtn", imgSrc: helpBtn.videoIcon, role: IS_TOUCH_DEVICE ? 'presentation' : 'menuitem', tabIndex: videoIdx === activeIndex ? 0 : -1, ariaLabel: "text.openVideoGuide", ariaLabelTranslated: t('text.openVideoGuide', { name: t(item.label) }), reference: function (el) {
                                                                if (el)
                                                                    itemRefs.current.set(videoIdx, el);
                                                            }, onFocus: function () { return setActiveIndex(videoIdx); }, clickHandler: function () {
                                                                return item.video && openVideo(item.video, item.label, item.id);
                                                            }, avoidDefaultStyle: true })), item.video && item.pdf && (_jsx("div", { className: "helpIconDivider", "aria-hidden": "true" })), item.pdf && (_jsx(ButtonComponent, { normalCSS: "helpIconBtn", imgSrc: helpBtn.pdfIcon, role: IS_TOUCH_DEVICE ? 'presentation' : 'menuitem', tabIndex: pdfIdx === activeIndex ? 0 : -1, ariaLabel: "text.openGuide", ariaLabelTranslated: t('text.openGuide', { name: t(item.label) }), reference: function (el) {
                                                                if (el)
                                                                    itemRefs.current.set(pdfIdx, el);
                                                            }, onFocus: function () { return setActiveIndex(pdfIdx); }, clickHandler: function () { return item.pdf && openPdf(item.pdf, item.label, item.id); }, avoidDefaultStyle: true }))] })] }) }, item.id));
                                }), (function () {
                                    var exitIdx = focusableIdx++;
                                    return (_jsx("div", { className: "helpBtnHolder helpBtnHolder--exit".concat(exitIdx === activeIndex ? ' helpBtnHolder--active' : ''), role: IS_TOUCH_DEVICE ? 'presentation' : 'menuitem', tabIndex: exitIdx === activeIndex ? 0 : -1, "aria-label": IS_TOUCH_DEVICE ? undefined : t('text.exitMenu'), ref: function (el) {
                                            if (el)
                                                itemRefs.current.set(exitIdx, el);
                                        }, onFocus: function () { return setActiveIndex(exitIdx); }, onClick: closePanel, children: _jsx("div", { className: "contentHolder", children: _jsx("div", { className: "text", children: t('text.exitMenu') }) }) }, "exitMenu"));
                                })()] }) }), _jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "srOnly", children: announcement })] }))] }));
};
export default HelpTool;
